import { useState, useEffect, useMemo, useRef } from 'react';
import { CourseOption } from '../../types/course.response';
import WeeklyScheduleGrid from './WeeklyScheduleGrid';
import Sidebar from './Sidebar';
import { generateColorMap } from '../../utils/colorUtils';
import { userCoursesApi } from '../../services/api/user-courses.api';
import { usersApi } from '../../services/api/users.api';
import { User } from '../../types/user.response';
import { getCourseCredit, getCourseKey, getUserItemId } from '../../utils/courseUtils';
import { autoAssignSections, classSectionIdsToNumbers } from '../../utils/scheduleUtils';
import Loading from '../../components/loading/Loading';
import { DEFAULT_SEMESTER } from '../../constants';

export default function Schedule() {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [selectedSemester, setSelectedSemester] = useState<string>(DEFAULT_SEMESTER);
  const [currentDateRange, setCurrentDateRange] = useState<string>("Aug 25 - 31, 2025");
  const [user, setUser] = useState<User | undefined>(undefined);
  const [userCourses, setUserCourses] = useState<CourseOption[]>([]);
  const [currentCourses, setCurrentCourses] = useState<CourseOption[]>([]);
  const attemptedSectionBackfillsRef = useRef<Set<string>>(new Set());
  const isBackfillingSectionsRef = useRef(false);
  
  const subjectColorMap = useMemo(() => {
    return generateColorMap(currentCourses, getCourseKey);
  }, [currentCourses]);

  const fetchUserCourses = async () => {
    setLoading(true);
    setError(undefined);
    try {
        const user = await usersApi.getUser();
        const userCourses = await userCoursesApi.getCourses();
        setUser(user);
        setUserCourses(userCourses);
    } catch (error) {
        setError("Error fetching program requirements");
    } finally {
        setLoading(false);
    }
  };

  const getCurrentCourses = (userCourses: CourseOption[]) => {
    return userCourses.filter(courseOption => courseOption.userState.semester === selectedSemester);
  };

  useEffect(() => {
    fetchUserCourses();
  }, []);

  useEffect(() => {
    setCurrentCourses(getCurrentCourses(userCourses));
  }, [selectedSemester, userCourses]);

  useEffect(() => {
    attemptedSectionBackfillsRef.current.clear();
  }, [selectedSemester]);

  useEffect(() => {
    const coursesMissingSections = currentCourses.filter(course =>
      course.userState.isScheduled &&
      course.userItemId !== null &&
      course.userState.sectionNumbers.length === 0 &&
      !attemptedSectionBackfillsRef.current.has(`${selectedSemester}:${course.userItemId}`)
    );

    if (coursesMissingSections.length === 0 || isBackfillingSectionsRef.current) {
      return;
    }

    let cancelled = false;

    const backfillMissingSections = async () => {
      isBackfillingSectionsRef.current = true;
      let workingCourses = [...userCourses];
      let backfillFailed = false;
      let hasBackfilledCourses = false;

      try {
        for (const course of coursesMissingSections) {
          if (course.userItemId === null) continue;

          attemptedSectionBackfillsRef.current.add(`${selectedSemester}:${course.userItemId}`);

          const classSectionIds = autoAssignSections(course, selectedSemester, workingCourses);
          if (classSectionIds.length === 0) continue;

          try {
            const { data: updatedCourse } = await userCoursesApi.updateCourse(course.userItemId, {
              classSectionIds,
            });

            workingCourses = workingCourses.map(existingCourse =>
              existingCourse.userItemId === course.userItemId ? updatedCourse : existingCourse
            );
            hasBackfilledCourses = true;
          } catch (error) {
            backfillFailed = true;
            console.error('Error auto-assigning missing sections:', error);
          }
        }
      } finally {
        isBackfillingSectionsRef.current = false;
        if (!cancelled && hasBackfilledCourses) {
          setUserCourses(workingCourses);
        }
        if (!cancelled && backfillFailed) {
          setError(prevError => prevError ?? 'Failed to auto-assign some missing sections');
        }
      }
    };

    void backfillMissingSections();

    return () => {
      cancelled = true;
    };
  }, [currentCourses, selectedSemester, userCourses]);

  const handleAddCourse = async (courseOption: CourseOption) => {
    setLoading(true);
    setError(undefined);
    try {
        const classSectionIds = autoAssignSections(courseOption, selectedSemester, userCourses);
        const creditsReceived = getCourseCredit(courseOption);
        await userCoursesApi.addCourse(
          courseOption.id,
          courseOption.topic,
          true, // isScheduled = true for schedule
          false, // isSaved = false for schedule
          selectedSemester,
          creditsReceived,
          classSectionIds,
          [],
        );
        await fetchUserCourses();
    } catch (error) {
        console.error('Error adding course:', error);
        setError(error instanceof Error ? error.message : 'An error occurred');
        throw error;
    } finally {
        setLoading(false);
    }
  };

  const handleToggleScheduled = async (courseOption: CourseOption) => {
    if (courseOption.userItemId === null) return;
    const newIsScheduled = !courseOption.userState.isScheduled;
    const userItemId = getUserItemId(courseOption);
    const originalSectionNumbers = courseOption.userState.sectionNumbers;

    let newClassSectionIds: number[] = [];
    let newSectionNumbers: string[] = [];

    if (newIsScheduled) {
      newClassSectionIds = autoAssignSections(courseOption, selectedSemester, userCourses);
      newSectionNumbers = classSectionIdsToNumbers(courseOption, selectedSemester, newClassSectionIds);
    }

    setUserCourses(prev =>
      prev.map(c =>
        getUserItemId(c) === userItemId
          ? { ...c, userState: { ...c.userState, isScheduled: newIsScheduled, sectionNumbers: newSectionNumbers } }
          : c
      )
    );

    try {
      await userCoursesApi.updateCourse(courseOption.userItemId, { 
        isScheduled: newIsScheduled,
        classSectionIds: newClassSectionIds,
      });
    } catch (error) {
      setUserCourses(prev =>
        prev.map(c =>
          getUserItemId(c) === userItemId
            ? { ...c, userState: { ...c.userState, isScheduled: !newIsScheduled, sectionNumbers: originalSectionNumbers } }
            : c
        )
      );
      setError(error instanceof Error ? error.message : 'Failed to update course');
    }
  };

  const handleDeleteCourse = async (courseOption: CourseOption) => {
    if (courseOption.userItemId === null) return;
    setLoading(true);
    setError(undefined);
    try {
        await userCoursesApi.deleteCourse(courseOption.userItemId);
        await fetchUserCourses();
    } catch (error) {
        console.error('Error deleting course:', error);
        setError(error instanceof Error ? error.message : 'An error occurred');
        throw error;
    } finally {
        setLoading(false);
    }
  };

  const weeklyScheduleRendered = useMemo(() => 
    <WeeklyScheduleGrid 
      colorMap={subjectColorMap}
      semester={selectedSemester}
      dateRange={[currentDateRange, currentDateRange]}
      currentCourses={currentCourses}
    />
  , [subjectColorMap, selectedSemester, currentDateRange, currentCourses]);

  const handleSectionSelect = async (course: CourseOption, type: string, sectionNbr: string) => {
    const courseKey = getCourseKey(course);
    const originalSections = course.userState.sectionNumbers;

    // Find all section numbers of the same type so we can swap them out
    const sameTypeSectionNumbers = new Set<string>();
    for (const eg of course.enrollGroups) {
      if (eg.semester !== selectedSemester) continue;
      for (const cs of eg.classSections) {
        if (cs.sectionType === type) {
          sameTypeSectionNumbers.add(cs.sectionNumber);
        }
      }
    }

    try {
      setUserCourses(prevCourses => {
        return prevCourses.map(existingCourse => {
          if (getCourseKey(existingCourse) !== courseKey) return existingCourse;

          let updatedSections = existingCourse.userState.sectionNumbers.filter(
            sn => !sameTypeSectionNumbers.has(sn)
          );
          updatedSections.push(sectionNbr);

          return {
            ...existingCourse,
            userState: {
              ...existingCourse.userState,
              sectionNumbers: updatedSections,
            },
          } as CourseOption;
        });
      });

      if (course.userItemId !== null) {
        const newSectionNumbers = [...originalSections.filter(s => !sameTypeSectionNumbers.has(s)), sectionNbr];
        const numberSet = new Set(newSectionNumbers);
        const classSectionIds = course.enrollGroups
          .filter(eg => eg.semester === selectedSemester)
          .flatMap(eg => eg.classSections)
          .filter(cs => numberSet.has(cs.sectionNumber))
          .map(cs => cs.id);

        await userCoursesApi.updateCourse(course.userItemId, { classSectionIds });
      }

    } catch (error) {
      setUserCourses(prevCourses => {
        return prevCourses.map(existingCourse => {
          if (getCourseKey(existingCourse) !== courseKey) return existingCourse;
          return {
            ...existingCourse,
            userState: {
              ...existingCourse.userState,
              sectionNumbers: originalSections,
            },
          } as CourseOption;
        });
      });
      console.error('Error updating section selection:', error);
      setError(error instanceof Error ? error.message : 'Failed to update section');
    }
  };
  
  return (
    <>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-50">
          <Loading size="lg" />
        </div>
      )}
      {error && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[1002] bg-red-50 border border-red-100 rounded px-4 py-3 shadow-md min-w-80">
          <p className="text-red-700 font-medium text-sm">{error}</p>
          <button className="mt-1 text-xs text-red-700 underline" onClick={() => setError(undefined)}>Dismiss</button>
        </div>
      )}
        {/* 主内容区域 */}
        <div className="flex flex-col md:flex-row pt-6 gap-1 max-w-[1200px] w-full mx-auto md:h-[calc(100vh-4rem)] md:overflow-hidden box-border">
          {/* Course sidebar: top on mobile, right on desktop */}
          <div className="w-full md:w-[350px] md:order-2 px-5 md:h-full md:overflow-hidden flex-shrink-0">
              <Sidebar
                user={user}
                currentCourses={currentCourses}
                semester={selectedSemester}
                colorMap={subjectColorMap}
                onAddCourse={handleAddCourse}
                onDeleteCourse={handleDeleteCourse}
                onSectionSelect={handleSectionSelect}
                onToggleScheduled={handleToggleScheduled}
              />
          </div>
          {/* Weekly grid: below on mobile, left on desktop */}
          <div className="w-full md:w-[860px] md:order-1 mx-auto flex-shrink-0 h-[60vh] md:h-full overflow-hidden">
            <div className="pt-3 pb-6 px-4 h-full overflow-hidden box-border">
              {weeklyScheduleRendered}
            </div>
          </div>
        </div>
    </>
  );
}
